/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import {
    Button,
    Card,
    Checkbox,
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    MoreButton,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
    FlaggedNoteForAuditStatus,
    INote,
    NoteLabels,
    NoteListSortAttribute,
    NoteStatus,
    NoteTypes,
} from "../../../../modules/notes/types/notes.types";
import { useFetchNotesList } from "../../../../hooks/queries/note";
import { CPT_CODES, LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import { useDebounce } from "../../../../hooks/helpers";
import {
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
    getTotalAmountOfTableRowsSelected,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    truncateString,
} from "../../../../utils/helpers";
import { UserPermisions, UserRole } from "../../../../utils/types/user";
import useShowInsuranceOverSelfPay from "../../../../modules/practice-settings/hooks/useShowInsuranceOverSelfPay";
import useToast from "../../../../hooks/useToast";
import {
    useChangeNoteArchiveStatus,
    useExportNotes,
} from "../../../../hooks/mutations/note";
import Navbar from "../../../Navbar/Navbar";
import ArchiveNote from "../../NoteContent/ArchiveNote/ArchiveNote";
import FlagNoteForm from "../../../../modules/notes/components/FlagNoteForm/FlagNoteForm";
import ListState from "../../../ListState/ListState";
import MobileListItem from "../../../../shared-ui/MobileListItem/MobileListItem";
import InsuranceProviderFilter, {
    getInsuranceProviderIdsFilter,
    setInsuranceProviderIdsFilter,
} from "../../../../shared-ui/InsuranceProviderFilter/InsuranceProviderFilter";
import TableFiltersDisplay from "../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import CheckPrimaryColorIcon from "../../../Icons/CheckPrimaryColor";
import styles from "../NoteListWrapper/NoteListWrapper.module.scss";
import {
    getCptCodeToDisplay,
    getFlaggedNoteStatusTag,
    getNoteTypeTextWithTag,
    getSelectedAuthorName,
    getPdfExportableNoteIds,
    isNotePdfExportable,
    updateAuthorFilterSearchParams,
} from "../../../../modules/notes/helpers/notes.helpers";
import { useExportPdfs } from "../../../../hooks/queries";
import { useAppSelector } from "../../../../hooks/useRedux";
import ExportLoaderAndText from "../../../../shared-ui/ExportLoaderAndText/ExportLoaderAndText";
import Loader from "../../../../shared-ui/Loader/Loader";
import { useFetchProviders } from "../../../../modules/providers/hooks/providers.queries";
import {
    useDoesUserHavePermission,
    useDoesUserHaveRole,
} from "../../../../hooks/permissions";

type Dialogs = "archive-note" | "flag_note" | "unflag_note" | "loader";

export default function FlaggedNotesList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );
    const [rowSelection, setRowSelection] = React.useState({});
    const [codeToConfirmId, setCodeToConfirmId] = React.useState<string | null>(
        null
    );

    const [notesCount, setNotesCount] = React.useState("");
    const [selectedNotesId, setSelectedNotesId] = React.useState<string[]>([]);
    const [showAuthorPopover, setShowAuthorPopover] = React.useState(false);
    const [providerNameSearchString, setProviderNameSearchString] =
        React.useState("");
    const [authorSearchString, setAuthorSearchString] = React.useState("");
    const [getSelectedData, setGetSelectedData] =
        React.useState<Row<INote>[]>();
    const [jobId, setJobId] = React.useState<string | null>(null);
    const [pdfCount, setPdfCount] = React.useState(0);

    const [sortConfig, setSortConfig] = React.useState<{
        key: string;
        direction: string;
    }>({
        key: searchParams.get("sort_attr") || "",
        direction: searchParams.get("sort_order") || "",
    });

    const { practice } = useAppSelector((state) => state.userPractice);

    // Get filters from url
    const clientFilter = searchParams.get("search_string") || "";
    const providerNameFilter = searchParams.get("provider_name") || "";
    const authorFilter = searchParams.get("author") || "";
    const authorNameFilter = searchParams.get("author_name") || "";
    const cptCodeFilter = searchParams.get("cpt_code") || "";
    const insuranceProviderIdsFilter =
        getInsuranceProviderIdsFilter(searchParams);
    const noteTypeFilter = searchParams.getAll("type[]");
    const flaggedStatusFilter = searchParams.getAll("flagged_status[]");
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");
    const showArchivedFilter = searchParams.get("show_archived") || "false";
    const fromDateCreatedFilter = searchParams.get("from_date_created");
    const toDateCreatedFilter = searchParams.get("to_date_created");
    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;

    const debouncedClienFilter = useDebounce(clientFilter);
    const debouncedProviderNameSearchString = useDebounce(
        providerNameSearchString
    );
    const debouncedAuthorSearchString = useDebounce(authorSearchString);

    // date filters values for DatePicker component
    // Converts the string values to a date in the correct timezone
    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);

    const fromDateCreatedFilterToDate = convertDateFilterStringToDate(
        fromDateCreatedFilter
    );
    const toDateCreatedFilterToDate =
        convertDateFilterStringToDate(toDateCreatedFilter);

    const handleResetProvidersSearchString = () => {
        setProviderNameSearchString("");
        setAuthorSearchString("");
    };

    const showInsOverSelfPay = useShowInsuranceOverSelfPay();

    const providers = useFetchProviders({
        search_string:
            debouncedProviderNameSearchString || debouncedAuthorSearchString,
        page: 1,
        limit: 20,
    });

    const handleDialogInView = (modal: Dialogs | null) => {
        setDialogInView(() => modal);
    };

    const sortableColumns = [
        "date_of_service",
        "client_name",
        "provider_name",
        "created_at",
        "author",
    ];

    const handleConfigAttribute = () => {
        if (sortableColumns.includes(sortConfig.key)) {
            return sortConfig.key as NoteListSortAttribute;
        }

        return undefined;
    };

    const statusOptions = Object.values(FlaggedNoteForAuditStatus);

    // Hook for fetching all notes
    const { data, isLoading, isSuccess, error } = useFetchNotesList(
        {
            cpt_code: cptCodeFilter,
            type: noteTypeFilter,
            from_date: fromDateFilter,
            to_date: toDateFilter,
            from_created_at_date: fromDateCreatedFilter,
            to_created_at_date: toDateCreatedFilter,
            search_string: debouncedClienFilter,
            provider_name: providerNameFilter,
            page: pageFilter,
            insurance_provider_ids: insuranceProviderIdsFilter,
            limit: limitFilter,
            show_archived: showArchivedFilter,
            sort_attribute:
                searchParams.get("sort_attr") || handleConfigAttribute(),
            sort_order: searchParams.get("sort_order") || sortConfig.direction,
            author_id: authorFilter,
            flagged_for_audit_status:
                flaggedStatusFilter.length > 0
                    ? flaggedStatusFilter
                    : statusOptions,
        },
        ""
    );

    const getAuthorTextToDisplay = getSelectedAuthorName(
        providers.data?.data,
        data?.data,
        authorFilter,
        authorNameFilter
    );

    const doesNoteHaveBill = (note: INote) => {
        const notesTypesWithoutBill = [
            NoteTypes.SAFETY_PLAN,
            NoteTypes.SUPERVISION_NOTE,
            NoteTypes.GENERAL_UPDATE_NOTE,
            NoteTypes.SUPERVISOR_CHECK_IN,
            NoteTypes.PAUSE_NOTIFICATION_NOTE,
        ];
        return (
            !notesTypesWithoutBill.includes(note.note_type) &&
            Boolean(note.date_of_service)
        );
    };

    const userShouldNaviagteToBills = [
        UserRole.BILLER,
        UserRole.SUPER_ADMIN,
        UserRole.BILLER_ADMIN,
    ].some((role) => practice?.roles?.includes(role));

    const navigate = useNavigate();

    // Clicked note navigation for web
    const handleRowClick = (row: Row<INote>) => {
        if (
            row.original.status === NoteStatus.CODED &&
            doesNoteHaveBill(row.original) &&
            userShouldNaviagteToBills
        ) {
            navigate(
                `/billing-and-claims/${row.original.client.client_id}/${row.original.note_id}?viewFrom=session_note`
            );
        } else {
            navigate(
                `/notes/${row.original.client.client_id}/${row.original.note_id}?viewFrom=session_note&noteListType=flagged_notes`
            );
        }
    };

    // Clicked note navigation for mobile
    const handleMobileNoteClick = (note: INote) => {
        if (
            note.status === NoteStatus.CODED &&
            doesNoteHaveBill(note) &&
            userShouldNaviagteToBills
        ) {
            navigate(
                `/billing-and-claims/${note.client.client_id}/${note.note_id}?viewFrom=session_note`
            );
        } else {
            navigate(
                `/notes/${note.client.client_id}/${note.note_id}?viewFrom=session_note`
            );
        }
    };

    /** FILTER INPUTS ONCHANGE FUNCTIONS */
    const handleSetClientNameFilter = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (e.target.value === "") searchParams.delete("search_string");
        else searchParams.set("search_string", e.target.value);
        searchParams.set("page", "1");
        localStorage.setItem("flaggednotes_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetAuthorFilter = (author: string, authorName = "") => {
        const nextSearchParams = updateAuthorFilterSearchParams(
            searchParams,
            author,
            authorName
        );
        localStorage.setItem(
            "flaggednotes_filters",
            nextSearchParams.toString()
        );
        setSearchParams(nextSearchParams);
    };

    const handleSetTypeFilter = (type: string) => {
        if (noteTypeFilter.includes(type)) {
            searchParams.delete("type[]", type);
        } else {
            searchParams.append("type[]", type);
        }
        searchParams.set("page", "1");
        localStorage.setItem("flaggednotes_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleFlaggedStatusFilterChange = (status: string) => {
        if (flaggedStatusFilter.includes(status)) {
            searchParams.delete("flagged_status[]", status);
        } else {
            searchParams.append("flagged_status[]", status);
        }
        searchParams.set("page", "1");
        localStorage.setItem("flaggednotes_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleInsuranceProviderIdsChange = (
        insuranceProviderIds: string[]
    ) => {
        setInsuranceProviderIdsFilter(searchParams, insuranceProviderIds);
        localStorage.setItem("flaggednotes_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetCptCodeFilter = (cptCode: string) => {
        if (cptCode === "") searchParams.delete("cpt_code");
        else searchParams.set("cpt_code", cptCode);
        localStorage.setItem("flaggednotes_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetArchivedNotesFilter = (archived: string) => {
        if (archived === "") searchParams.delete("show_archived");
        else searchParams.set("show_archived", archived);
        localStorage.setItem("flaggednotes_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleFromDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date");
        else searchParams.set("from_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem("flaggednotes_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem("flaggednotes_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleFromDateCreatedFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date_created");
        else searchParams.set("from_date_created", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem("flaggednotes_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleToDateCreatedFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date_created");
        else searchParams.set("to_date_created", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem("flaggednotes_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetPageFilter = (page: number) => {
        searchParams.set("page", String(page));
        localStorage.setItem("flaggednotes_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetLimitFilter = (limit: number) => {
        searchParams.set("limit", String(limit));
        localStorage.setItem("flaggednotes_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const getArchivedNoteFilterToDisplay = () => {
        if (showArchivedFilter === "true") {
            return "Archived notes";
        }
        return "Unarchived notes";
    };

    const selectedIds = getSelectedData?.map(
        (item: Row<INote>) => item.original.note_id
    );

    const mobileListRef = React.useRef<HTMLDivElement>(null);

    // Array of details of the selected notes(web)
    const notesDetails =
        selectedIds &&
        selectedIds.map((noteId: string) =>
            data?.data.find((note) => note.note_id === noteId)
        );

    // Array of details of the selected notes(mobile)
    const selectedNotesDetails =
        selectedNotesId &&
        selectedNotesId.map((noteId: string) =>
            data?.data.find((note) => note.note_id === noteId)
        );

    const detailsArray =
        selectedNotesId.length > 0 ? selectedNotesDetails : notesDetails;
    const hasExportableSelectedNote =
        detailsArray?.some(
            (note) => note !== undefined && isNotePdfExportable(note)
        ) ?? false;

    // Details of a single note
    const noteDetails = data?.data.find((note) => {
        return note.note_id === codeToConfirmId;
    });

    const { toast } = useToast();

    const queryClient = useQueryClient();

    const unarchiveNote = useChangeNoteArchiveStatus();

    const archiveNoteIsMutating = useIsMutating(["archive-note"]);
    const flagNoteIsMutating = useIsMutating(["flag-note"]);

    const handleUnarchive = (id?: string) => {
        const noteIds =
            selectedIds && selectedIds.length > 0
                ? selectedIds
                : selectedNotesId;

        const dataToSend = {
            note_ids_to_archive: noteIds.length === 0 && id ? [id] : noteIds,
            archive_status: false,
        };

        unarchiveNote.mutate(dataToSend, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries({
                    queryKey: [`notes`],
                });

                await queryClient.invalidateQueries({
                    queryKey: ["supervisee"],
                });

                toast({
                    mode: "success",
                    message: res.message || "Note unarchived successfully",
                });

                setRowSelection({});
                setSelectedNotesId([]);
            },

            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not unarchive note at this time",
                });
            },
        });
    };

    const exportNotes = useExportNotes();

    const canExportNotes = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_WRITE,
        UserPermisions.CLIENT_INFO_NOTES_WRITE,
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
    ]);

    const handleExportNotes = (noteId?: string) => {
        let notesToExport: INote[];
        if (selectedIds && selectedIds.length > 0) {
            notesToExport = getSelectedData?.map((item) => item.original) ?? [];
        } else if (selectedNotesId.length > 0) {
            notesToExport = selectedNotesDetails.filter(
                (note): note is INote => note !== undefined
            );
        } else {
            notesToExport =
                data?.data.filter((note) => note.note_id === noteId) ?? [];
        }
        const exportableNoteIds = getPdfExportableNoteIds(notesToExport);

        if (exportableNoteIds.length === 0) {
            toast({
                mode: "warning",
                message: "The selected notes are unavailable for PDF export",
            });
            return;
        }

        const dataToSend = { session_note_ids: exportableNoteIds };

        exportNotes.mutate(dataToSend, {
            onSuccess: async (res) => {
                handleDialogInView("loader");
                await new Promise<void>((resolve) => {
                    const timer = setTimeout(() => {
                        clearTimeout(timer);
                        setJobId(res.data.export_job_id);
                        setPdfCount(res.data.expected_pdf_count);
                        resolve();
                    }, 8000);
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.message || "Could not export notes at this time",
                });
            },
        });
    };

    const exportNote = useExportPdfs(
        {
            export_job_id: jobId as string,
            pdf_type: "session_note",
            expected_pdf_count: pdfCount,
        },
        Boolean(jobId)
    );

    if (jobId && !exportNote.isLoading) {
        setJobId(null);
        handleDialogInView(null);
        setRowSelection({});
    }

    const canArchiveNote = [
        UserPermisions.CLIENT_NOTES_ALL_READ,
        UserPermisions.CLIENT_INFO_NOTES_WRITE,
    ].some((permission) => practice?.permissions?.includes(permission));

    const canArchiveCodedNote = [
        UserRole.SUPER_ADMIN,
        UserRole.CODER,
        UserRole.BILLER,
        UserRole.BILLER_ADMIN,
    ].some((role) => practice?.roles?.includes(role));

    const showArchiveOption = (row: Row<INote>) =>
        (canArchiveNote &&
            (row.original.status !== NoteStatus.CODED ||
                !row.original.date_of_service)) ||
        (canArchiveCodedNote && row.original.status === NoteStatus.CODED);

    const selectedNotesHaveClaim = detailsArray?.some((note) =>
        Boolean(note?.claim_id)
    );

    const isBillerOrBillerAdmin = useDoesUserHaveRole([
        UserRole.BILLER,
        UserRole.BILLER_ADMIN,
    ]);

    const canArchiveNoteWithClaim = (note: INote) =>
        !note.claim_id || isBillerOrBillerAdmin;

    const canArchiveSelectedNotesWithClaim = () =>
        !selectedNotesHaveClaim || isBillerOrBillerAdmin;

    const showBulkArchiveBtn = () =>
        (canArchiveNote &&
            detailsArray?.every(
                (note) =>
                    note?.status !== NoteStatus.CODED || !note?.date_of_service
            )) ||
        (canArchiveCodedNote &&
            detailsArray?.every((note) => note?.status === NoteStatus.CODED));

    const showBulkFlagNotesBtn =
        practice?.permissions?.includes(
            UserPermisions.CLIENT_INFO_NOTES_FLAG_FOR_AUDIT_WRITE
        ) &&
        detailsArray?.every(
            (note) =>
                note?.flagged_for_audit_status ===
                FlaggedNoteForAuditStatus.UNFLAGGED
        ) &&
        detailsArray?.every(
            (note) =>
                note?.status === NoteStatus.PENDING_CODE_REVIEW ||
                note?.status === NoteStatus.CODED
        ) &&
        !practice?.roles?.includes(UserRole.AUDITOR_ADMIN);

    const showBulkUnflagNotesBtn =
        practice?.permissions?.includes(
            UserPermisions.CLIENT_INFO_NOTES_FLAG_FOR_AUDIT_WRITE
        ) &&
        !practice?.roles?.includes(UserRole.AUDITOR_ADMIN) &&
        !detailsArray?.every(
            (note) =>
                note?.flagged_for_audit_status ===
                FlaggedNoteForAuditStatus.UNFLAGGED
        );

    const showFlagOption = (row: Row<INote>) =>
        row.original.flagged_for_audit_status !==
            FlaggedNoteForAuditStatus.FLAGGED &&
        (row.original.status === NoteStatus.CODED ||
            row.original.status === NoteStatus.PENDING_CODE_REVIEW) &&
        practice?.permissions?.includes(
            UserPermisions.CLIENT_INFO_NOTES_FLAG_FOR_AUDIT_WRITE
        ) &&
        !practice?.roles?.includes(UserRole.AUDITOR_ADMIN);

    const showBulkUnflagOption = (row: Row<INote>) =>
        practice?.permissions?.includes(
            UserPermisions.CLIENT_INFO_NOTES_FLAG_FOR_AUDIT_WRITE
        ) &&
        !practice?.roles?.includes(UserRole.AUDITOR_ADMIN) &&
        row.original.flagged_for_audit_status ===
            FlaggedNoteForAuditStatus.FLAGGED;

    const handleNoteHasMoreActions = (row: Row<INote>) =>
        canExportNotes ||
        showArchiveOption(row) ||
        showFlagOption(row) ||
        showBulkUnflagOption(row);

    // Toggles note selection for mobile
    const toggleNoteSelection = (noteId: string) => {
        if (selectedNotesId.includes(noteId)) {
            setSelectedNotesId(selectedNotesId.filter((id) => id !== noteId));
        } else {
            setSelectedNotesId([...selectedNotesId, noteId]);
        }
    };

    const columns: ColumnDef<INote>[] = React.useMemo(
        () => [
            {
                id: "select",
                header: ({ table }) => (
                    <Checkbox
                        handleChange={table.getToggleAllRowsSelectedHandler()}
                        indeterminate={table.getIsSomeRowsSelected()}
                        label=""
                        name="row"
                        value="row"
                        isChecked={table.getIsAllRowsSelected()}
                    />
                ),
                cell: ({ row }) => (
                    <Checkbox
                        handleChange={row.getToggleSelectedHandler()}
                        indeterminate={row.getIsSomeSelected()}
                        label=""
                        name="row"
                        value="row"
                        isChecked={row.getIsSelected()}
                    />
                ),
            },
            {
                accessorKey: "date_of_service",
                header: "DATE OF SERVICE",
                cell: ({ row }) =>
                    row.original.date_of_service
                        ? formatDate(row.original.date_of_service)
                        : "--",
            },
            {
                accessorKey: "note_type",
                header: "NOTE TYPE",
                cell: ({ row }) =>
                    getNoteTypeTextWithTag(row.original.note_type),
            },
            {
                accessorKey: "client_name",
                header: "CLIENT",
                cell: ({ row }) => (
                    <span className="capitalize">
                        {displayNameInRightFormat({
                            firstName: row.original.client?.first_name,
                            lastName: row.original.client?.last_name,
                        })}
                    </span>
                ),
            },
            {
                accessorKey: "cpt_code",
                header: "CPT CODE(S)",
                cell: ({ row }) =>
                    getCptCodeToDisplay({
                        noteType: row.original.note_type,
                        cptCode: row.original.cpt_code,
                        additionalCptCodes: row.original.additional_cpt_codes,
                        unitCalculation:
                            row.original?.note_metadata?.unit_calculation,
                    }),
            },
            {
                accessorKey: "external_reference_id",
                header: "claim ref no",
                cell: ({ row }) => row.original.external_reference_id ?? "--",
            },
            {
                accessorKey: "author",
                header: "CLINICIAN",
                cell: ({ row }) => (
                    <span className="capitalize">
                        {displayNameInRightFormat({
                            firstName: row.original.author?.first_name,
                            lastName: row.original.author?.last_name,
                        })}
                    </span>
                ),
            },
            {
                accessorKey: "created_at",
                header: "CREATED ON",
                cell: ({ row }) => formatDate(row.original.created_at),
            },
            {
                accessorKey: "flagged_note_status",
                header: "STATUS",
                cell: ({ row }) =>
                    row.original.flagged_for_audit_status
                        ? getFlaggedNoteStatusTag(
                              row.original.flagged_for_audit_status
                          )
                        : "--",
            },
            {
                accessorKey: "client",
                header: "INSURANCE PROVIDER",
                cell: ({ row }) => (
                    <span
                        className={
                            row.original.client.self_pay && !showInsOverSelfPay
                                ? "normal-case"
                                : "uppercase"
                        }
                    >
                        {row.original.client.self_pay && !showInsOverSelfPay
                            ? "Self pay"
                            : (row.original.client.insurance_provider_name &&
                                  removeEnumUnderscore(
                                      row.original.client
                                          .insurance_provider_name || ""
                                  )) ||
                              "--"}
                    </span>
                ),
            },
            {
                accessorKey: "note_id",
                header: "",
                cell: ({ row }) =>
                    handleNoteHasMoreActions(row) && (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <MoreButton
                                    onPointerDown={() => {
                                        setCodeToConfirmId(
                                            () => row.original.note_id
                                        );
                                    }}
                                />
                            </DropdownTrigger>
                            <DropdownContent align="end" width="auto">
                                {showArchiveOption(row) &&
                                    canArchiveNoteWithClaim(row.original) &&
                                    row.original.is_archived === false &&
                                    row.original.status !==
                                        NoteStatus.DRAFT && (
                                        <DropdownItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDialogInView(
                                                    "archive-note"
                                                );
                                            }}
                                        >
                                            Archive note
                                        </DropdownItem>
                                    )}
                                {showArchiveOption(row) &&
                                    row.original.is_archived && (
                                        <DropdownItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleUnarchive(
                                                    row.original.note_id
                                                );
                                            }}
                                        >
                                            Unarchive note
                                        </DropdownItem>
                                    )}
                                {showFlagOption(row) && (
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDialogInView("flag_note");
                                        }}
                                    >
                                        Flag for audit
                                    </DropdownItem>
                                )}
                                {showBulkUnflagOption(row) && (
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDialogInView("unflag_note");
                                        }}
                                    >
                                        Unflag note
                                    </DropdownItem>
                                )}
                                {canExportNotes &&
                                    isNotePdfExportable(row.original) && (
                                        <DropdownItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleExportNotes(
                                                    row.original.note_id
                                                );
                                            }}
                                        >
                                            Export to pdf
                                        </DropdownItem>
                                    )}
                            </DropdownContent>
                        </Dropdown>
                    ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    React.useEffect(() => {
        if (setNotesCount) {
            if (error) setNotesCount("");
            else if (isLoading) setNotesCount("");
            else if (data && data?.total_count > 0) {
                setNotesCount(String(data.total_count));
            } else if (data && data.data.length === 0) {
                setNotesCount("");
            }
        }
    }, [data, error, isLoading, setNotesCount]);

    React.useEffect(() => {
        searchParams.set("sort_attr", sortConfig.key);
        searchParams.set("sort_order", sortConfig.direction);
        localStorage.setItem("flaggednotes_filters", searchParams.toString());
        setSearchParams(searchParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortConfig]);

    return (
        <>
            <Dialog open={dialogInView === "archive-note"}>
                <DialogContent
                    title="Archive Note"
                    variant="center"
                    showFooter
                    saveText="Archive Note"
                    submitBtnFormValue="archive-note"
                    handleCloseDialog={() => handleDialogInView(null)}
                    isSubmitBtnDisabled={archiveNoteIsMutating > 0}
                    isCancelBtnDisabled={archiveNoteIsMutating > 0}
                >
                    <ArchiveNote
                        onFormSubmit={() => {
                            handleDialogInView(null);
                            setRowSelection({});
                            setSelectedNotesId([]);
                        }}
                        noteId={noteDetails?.note_id as string}
                        selectedIds={selectedIds}
                    />
                </DialogContent>
            </Dialog>
            <Dialog
                open={
                    dialogInView === "flag_note" ||
                    dialogInView === "unflag_note"
                }
            >
                <DialogContent
                    title={
                        dialogInView === "flag_note"
                            ? "Flag for Audit"
                            : "Unflag Note for Audit"
                    }
                    variant="center"
                    showFooter
                    saveText={
                        dialogInView === "flag_note"
                            ? "Flag for Audit"
                            : "Unflag Note for Audit"
                    }
                    submitBtnFormValue="flag-note"
                    handleCloseDialog={() => {
                        handleDialogInView(null);
                        setRowSelection({});
                    }}
                    isSubmitBtnDisabled={flagNoteIsMutating > 0}
                    isCancelBtnDisabled={flagNoteIsMutating > 0}
                >
                    <FlagNoteForm
                        onFormSubmit={() => {
                            handleDialogInView(null);
                            setRowSelection({});
                            setSelectedNotesId([]);
                        }}
                        noteId={noteDetails?.note_id}
                        selectedIds={selectedIds}
                        action={
                            dialogInView === "flag_note" ? "flag" : "unflag"
                        }
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "loader"}>
                <DialogContent
                    title="Export in progress"
                    variant="center"
                    handleCloseDialog={() => handleDialogInView(null)}
                    showFooter={false}
                >
                    <ExportLoaderAndText />
                </DialogContent>
            </Dialog>
            <Navbar title={`Flagged Notes (${notesCount})`} />
            <div className="page">
                <Card classNames="mb-[--spacing-header]">
                    <div className="flex flex-col gap-[--spacing-items]">
                        <div className="lex items-center justify-between flex-wrap gap-[--spacing-items]">
                            <SearchInput
                                defaultValue={clientFilter}
                                onChange={handleSetClientNameFilter}
                                containerClass="w-[400px] max-w-full"
                                placeholder="Search by client name or chart number"
                            />
                        </div>
                        <div className="flex items-center justify-between flex-wrap gap-[--spacing-items]">
                            <div className="flex items-center gap-[--spacing-items] flex-wrap">
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>Note type</FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent align="end" width="auto">
                                        {Object.values(NoteTypes).map(
                                            (type) => (
                                                <DropdownItem
                                                    key={type}
                                                    onClick={() =>
                                                        handleSetTypeFilter(
                                                            type
                                                        )
                                                    }
                                                    isMulti
                                                    itemValue={type}
                                                    values={noteTypeFilter}
                                                >
                                                    {makeStringFirstLetterCapital(
                                                        removeEnumUnderscore(
                                                            NoteLabels[type]
                                                        )
                                                    )}
                                                </DropdownItem>
                                            )
                                        )}
                                    </DropdownContent>
                                </Dropdown>

                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>Status</FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent align="end" width="auto">
                                        {statusOptions.map((status) => (
                                            <DropdownItem
                                                key={status}
                                                onClick={() =>
                                                    handleFlaggedStatusFilterChange(
                                                        status
                                                    )
                                                }
                                                isMulti
                                                itemValue={status}
                                                values={flaggedStatusFilter}
                                            >
                                                {makeStringFirstLetterCapital(
                                                    removeEnumUnderscore(status)
                                                )}
                                            </DropdownItem>
                                        ))}
                                    </DropdownContent>
                                </Dropdown>

                                <InsuranceProviderFilter
                                    values={insuranceProviderIdsFilter}
                                    onChange={handleInsuranceProviderIdsChange}
                                />

                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton className="normal-case">
                                            {cptCodeFilter || "CPT code"}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent>
                                        <DropdownItem
                                            isSelected={!cptCodeFilter}
                                            onClick={() =>
                                                handleSetCptCodeFilter("")
                                            }
                                        >
                                            All
                                        </DropdownItem>
                                        {CPT_CODES.map((code) => (
                                            <DropdownItem
                                                key={code}
                                                isSelected={
                                                    cptCodeFilter === code
                                                }
                                                onClick={() =>
                                                    handleSetCptCodeFilter(code)
                                                }
                                            >
                                                {removeEnumUnderscore(code)}
                                            </DropdownItem>
                                        ))}
                                    </DropdownContent>
                                </Dropdown>

                                <Popover
                                    open={showAuthorPopover}
                                    onOpenChange={(open) => {
                                        setShowAuthorPopover(open);
                                        handleResetProvidersSearchString();
                                    }}
                                >
                                    <PopoverTrigger
                                        asChild
                                        onClick={() =>
                                            setShowAuthorPopover(true)
                                        }
                                    >
                                        <FilterButton
                                            className={
                                                authorFilter
                                                    ? "capitalize"
                                                    : "normal-case"
                                            }
                                        >
                                            {getAuthorTextToDisplay
                                                ? truncateString(
                                                      getAuthorTextToDisplay
                                                  )
                                                : "Clinician"}
                                        </FilterButton>
                                    </PopoverTrigger>
                                    <PopoverContent align="start">
                                        <SearchInput
                                            placeholder="Search by name"
                                            onChange={(e) =>
                                                setAuthorSearchString(
                                                    e.target.value
                                                )
                                            }
                                        />

                                        <div className="flex flex-col items-start max-h-[230px] overflow-y-auto">
                                            {providers.isLoading ? (
                                                <Loader wrapperClassName="h-[150px]" />
                                            ) : null}
                                            {providers.error && (
                                                <ListState
                                                    isError
                                                    context="general"
                                                    stateHelperText="Please try again later"
                                                    errorMsg={
                                                        providers.error
                                                            ?.response?.data
                                                            ?.message ||
                                                        "Error loading authors"
                                                    }
                                                    cardClass="h-[150px]"
                                                />
                                            )}
                                            {providers.data?.data &&
                                                providers.isSuccess && (
                                                    <>
                                                        <Button
                                                            size="auto"
                                                            variant="can-hover"
                                                            className="font-medium capitalize w-full justify-start my-1.5 shrink-0"
                                                            onClick={() => {
                                                                handleSetAuthorFilter(
                                                                    ""
                                                                );
                                                                setShowAuthorPopover(
                                                                    false
                                                                );
                                                                handleResetProvidersSearchString();
                                                            }}
                                                        >
                                                            All
                                                        </Button>
                                                        {providers?.data?.data.map(
                                                            (provider) => (
                                                                <Button
                                                                    key={
                                                                        provider.provider_id
                                                                    }
                                                                    size="auto"
                                                                    variant="can-hover"
                                                                    className="font-medium capitalize w-full justify-start mb-1.5 shrink-0"
                                                                    onClick={() => {
                                                                        handleSetAuthorFilter(
                                                                            provider.user_id,
                                                                            provider.provider_name
                                                                        );
                                                                        setShowAuthorPopover(
                                                                            false
                                                                        );
                                                                        handleResetProvidersSearchString();
                                                                    }}
                                                                >
                                                                    {displayNameInRightFormat(
                                                                        provider.provider_name
                                                                    )}
                                                                    {authorFilter ===
                                                                    provider.user_id ? (
                                                                        <CheckPrimaryColorIcon />
                                                                    ) : null}
                                                                </Button>
                                                            )
                                                        )}
                                                    </>
                                                )}
                                        </div>
                                    </PopoverContent>
                                </Popover>

                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton className="normal-case">
                                            {getArchivedNoteFilterToDisplay()}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent>
                                        <DropdownItem
                                            isSelected={
                                                showArchivedFilter === "true"
                                            }
                                            onClick={() =>
                                                handleSetArchivedNotesFilter(
                                                    "true"
                                                )
                                            }
                                        >
                                            Archived notes
                                        </DropdownItem>
                                        <DropdownItem
                                            isSelected={
                                                showArchivedFilter === "false"
                                            }
                                            onClick={() =>
                                                handleSetArchivedNotesFilter(
                                                    "false"
                                                )
                                            }
                                        >
                                            Unarchived notes
                                        </DropdownItem>
                                    </DropdownContent>
                                </Dropdown>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <FilterButton>
                                            Date Created
                                        </FilterButton>
                                    </PopoverTrigger>
                                    <PopoverContent align="end">
                                        <div className="mb-1">
                                            <DatePicker
                                                label="From"
                                                placeholderText="MM/DD/YYYY"
                                                selected={
                                                    fromDateCreatedFilterToDate
                                                }
                                                onChange={
                                                    handleFromDateCreatedFilterChange
                                                }
                                                maxDate={
                                                    toDateCreatedFilterToDate ||
                                                    new Date(Date.now())
                                                }
                                                isClearable
                                            />
                                        </div>
                                        <DatePicker
                                            label="To"
                                            placeholderText="MM/DD/YYYY"
                                            selected={toDateCreatedFilterToDate}
                                            onChange={
                                                handleToDateCreatedFilterChange
                                            }
                                            minDate={
                                                fromDateCreatedFilterToDate
                                            }
                                            isClearable
                                        />
                                    </PopoverContent>
                                </Popover>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <FilterButton>
                                            Date Of Service
                                        </FilterButton>
                                    </PopoverTrigger>
                                    <PopoverContent align="end">
                                        <div className="mb-1">
                                            <DatePicker
                                                label="From"
                                                placeholderText="MM/DD/YYYY"
                                                selected={fromDateFilterToDate}
                                                onChange={
                                                    handleFromDateFilterChange
                                                }
                                                maxDate={
                                                    toDateFilterToDate ||
                                                    new Date(Date.now())
                                                }
                                                isClearable
                                            />
                                        </div>
                                        <DatePicker
                                            label="To"
                                            placeholderText="MM/DD/YYYY"
                                            selected={toDateFilterToDate}
                                            onChange={handleToDateFilterChange}
                                            minDate={fromDateFilterToDate}
                                            isClearable
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                    </div>
                    <TableFiltersDisplay
                        appliedFilters={[
                            {
                                key: "type[]",
                                values: noteTypeFilter.map(
                                    (type) => NoteLabels[type]
                                ),
                            },
                            {
                                key: "flagged_status[]",
                                values: flaggedStatusFilter,
                            },
                        ]}
                        wrapperClass="mt-[--spacing-items]"
                        localStorageKey="flaggednotes_filters"
                    />
                </Card>

                {isLoading ? <Loader /> : null}
                {error && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error?.response?.data.message ||
                            `Cannot display notes list at this time please try again later`
                        }
                    />
                )}
                {data && isSuccess && data.data.length === 0 && (
                    <ListState
                        stateHelperText="Notes list will appear here once they are added"
                        isError={false}
                        emptyMessage="No note added yet"
                    />
                )}

                {isSuccess && data && data.data.length > 0 && (
                    <>
                        {getTotalAmountOfTableRowsSelected(rowSelection)
                            .amount > 0 && (
                            <div className="flex items-center mb-[--spacing-items] gap-[--spacing-items] flex-wrap">
                                <div className="font-medium text-base shrink-0">
                                    {
                                        getTotalAmountOfTableRowsSelected(
                                            rowSelection
                                        ).amountString
                                    }
                                    :
                                </div>

                                {canExportNotes && hasExportableSelectedNote && (
                                    <Button
                                        variant="secondary"
                                        onClick={() => handleExportNotes()}
                                    >
                                        Export to pdf
                                    </Button>
                                )}

                                {showBulkArchiveBtn() &&
                                    detailsArray?.every(
                                        (note) => note?.is_archived === false
                                    ) &&
                                    canArchiveSelectedNotesWithClaim() &&
                                    detailsArray?.every(
                                        (note) =>
                                            note?.status !== NoteStatus.DRAFT
                                    ) && (
                                        <Button
                                            variant="secondary"
                                            onClick={() =>
                                                handleDialogInView(
                                                    "archive-note"
                                                )
                                            }
                                        >
                                            Archive note
                                        </Button>
                                    )}

                                {showBulkArchiveBtn() &&
                                    detailsArray?.every(
                                        (note) => note?.is_archived
                                    ) && (
                                        <Button
                                            variant="secondary"
                                            onClick={() => handleUnarchive()}
                                        >
                                            Unarchive note
                                        </Button>
                                    )}

                                {showBulkFlagNotesBtn && (
                                    <Button
                                        variant="secondary"
                                        onClick={() =>
                                            handleDialogInView("flag_note")
                                        }
                                    >
                                        Flag for audit
                                    </Button>
                                )}
                                {showBulkUnflagNotesBtn && (
                                    <Button
                                        variant="secondary"
                                        onClick={() =>
                                            handleDialogInView("unflag_note")
                                        }
                                    >
                                        Unflag note
                                    </Button>
                                )}
                            </div>
                        )}
                        {selectedNotesId.length > 0 && (
                            <div className="mb-16 flex items-center gap-x-8 text-sm">
                                <div>
                                    {`${selectedNotesId.length}  selected`} :
                                </div>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>Actions</FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent width="auto">
                                        <div
                                            className={
                                                styles.action_dropdown_items
                                            }
                                        >
                                            {canExportNotes &&
                                                hasExportableSelectedNote && (
                                                    <DropdownItem
                                                        onClick={() =>
                                                            handleExportNotes()
                                                        }
                                                    >
                                                        Export to pdf
                                                    </DropdownItem>
                                                )}

                                            {showBulkArchiveBtn() &&
                                                detailsArray?.every(
                                                    (note) =>
                                                        note?.is_archived ===
                                                        false
                                                ) &&
                                                canArchiveSelectedNotesWithClaim() &&
                                                detailsArray?.every(
                                                    (note) =>
                                                        note?.status !==
                                                        NoteStatus.DRAFT
                                                ) && (
                                                    <DropdownItem
                                                        onClick={() =>
                                                            handleDialogInView(
                                                                "archive-note"
                                                            )
                                                        }
                                                    >
                                                        Archive note
                                                    </DropdownItem>
                                                )}
                                            {showBulkFlagNotesBtn && (
                                                <DropdownItem
                                                    onClick={() =>
                                                        handleDialogInView(
                                                            "flag_note"
                                                        )
                                                    }
                                                >
                                                    Flag for audit
                                                </DropdownItem>
                                            )}
                                            {practice?.permissions?.includes(
                                                UserPermisions.CLIENT_INFO_NOTES_FLAG_FOR_AUDIT_WRITE
                                            ) &&
                                                !detailsArray?.every(
                                                    (note) =>
                                                        note?.flagged_for_audit_status ===
                                                        FlaggedNoteForAuditStatus.UNFLAGGED
                                                ) && (
                                                    <DropdownItem
                                                        onClick={() =>
                                                            handleDialogInView(
                                                                "unflag_note"
                                                            )
                                                        }
                                                    >
                                                        Unflag note
                                                    </DropdownItem>
                                                )}
                                            {showBulkArchiveBtn() &&
                                                detailsArray?.every(
                                                    (note) => note?.is_archived
                                                ) && (
                                                    <DropdownItem
                                                        onClick={() =>
                                                            handleUnarchive()
                                                        }
                                                    >
                                                        Unarchive note
                                                    </DropdownItem>
                                                )}
                                        </div>
                                    </DropdownContent>
                                </Dropdown>
                            </div>
                        )}
                        <div>
                            <Table
                                columns={columns}
                                data={data?.data}
                                rowSelection={rowSelection}
                                setRowSelection={setRowSelection}
                                handleRowClick={handleRowClick}
                                setGetSelectedData={setGetSelectedData}
                                className={styles.table}
                                setSortConfig={setSortConfig}
                                sortConfig={sortConfig}
                                sortableColumns={sortableColumns}
                                hasPagination={data.total_count > 20}
                                pagination={
                                    <Pagination
                                        totalCount={data.total_count}
                                        totalPages={data.total_pages}
                                        currentPage={pageFilter}
                                        onPageChange={handleSetPageFilter}
                                        limit={limitFilter}
                                        onLimitChange={handleSetLimitFilter}
                                    />
                                }
                            />
                        </div>
                        <div
                            role="table"
                            ref={mobileListRef}
                            className="md:hidden"
                        >
                            <div className="flex flex-col gap-y-12">
                                {data.data.map((note) => (
                                    <MobileListItem
                                        key={note.note_id}
                                        onClickCheckButton={() =>
                                            toggleNoteSelection(note.note_id)
                                        }
                                        isSelected={selectedNotesId.includes(
                                            note.note_id
                                        )}
                                        showCheckButton
                                        topChildren={
                                            <div className="flex items-center gap-x-8 text-base font-semibold capitalize">
                                                {displayNameInRightFormat({
                                                    firstName:
                                                        note.client.first_name,
                                                    lastName:
                                                        note.client.last_name,
                                                })}
                                            </div>
                                        }
                                        bottomChildren={
                                            <Button
                                                variant="normal"
                                                size="auto"
                                                className="flex flex-col gap-y-8 w-full items-start"
                                                onClick={() =>
                                                    handleMobileNoteClick(note)
                                                }
                                            >
                                                <div className="flex items-center w-full text-left gap-x-4">
                                                    <span className="basis-1/2 text-gray font-semibold text-xs">
                                                        {formatDate(
                                                            note.date_of_service
                                                        )}
                                                    </span>
                                                    <span className="basis-1/2 font-semibold text-xs">
                                                        {
                                                            NoteLabels[
                                                                note.note_type
                                                            ]
                                                        }
                                                    </span>
                                                </div>
                                                <div className="flex items-center w-full text-left gap-x-4">
                                                    <span className="w-1/2 text-gray font-semibold text-xs">
                                                        Provider:
                                                    </span>
                                                    <span className="w-1/2 font-semibold text-xs">
                                                        {note.provider
                                                            ? displayNameInRightFormat(
                                                                  {
                                                                      firstName:
                                                                          note
                                                                              .provider
                                                                              ?.first_name,
                                                                      lastName:
                                                                          note
                                                                              .provider
                                                                              ?.last_name,
                                                                  }
                                                              )
                                                            : "--"}
                                                    </span>
                                                </div>
                                                <div className="flex items-center w-full text-left gap-x-4">
                                                    <span className="w-1/2 text-gray font-semibold text-xs">
                                                        Created on:
                                                    </span>
                                                    <span className="w-1/2 font-semibold text-xs">
                                                        {formatDate(
                                                            note.created_at
                                                        )}
                                                    </span>
                                                </div>
                                                {!practice?.provider_id && (
                                                    <div className="flex items-center w-full text-left gap-x-4">
                                                        <span className="w-1/2 text-gray font-semibold text-xs">
                                                            Insurance Provider:
                                                        </span>
                                                        <span
                                                            className={
                                                                note.client
                                                                    .self_pay
                                                                    ? "normal-case w-1/2 font-semibold text-xs"
                                                                    : "uppercase w-1/2 font-semibold text-xs"
                                                            }
                                                        >
                                                            {note.client
                                                                .self_pay &&
                                                            !showInsOverSelfPay
                                                                ? "Self pay"
                                                                : (note.client
                                                                      .insurance_provider_name &&
                                                                      removeEnumUnderscore(
                                                                          note
                                                                              .client
                                                                              .insurance_provider_name ||
                                                                              ""
                                                                      )) ||
                                                                  "--"}
                                                        </span>
                                                    </div>
                                                )}
                                            </Button>
                                        }
                                    />
                                ))}
                            </div>
                            {data.total_count > 20 && (
                                <div className="bg-white p-12 rounded-r8 mt-16">
                                    <Pagination
                                        totalCount={data.total_count}
                                        totalPages={data.total_pages}
                                        currentPage={pageFilter}
                                        onPageChange={handleSetPageFilter}
                                        limit={limitFilter}
                                        onLimitChange={handleSetLimitFilter}
                                    />
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
